import { asyncCallback } from "../utils/analysis-async";
import { StepProcess } from "../interfaces/stepProcess";
import {
  Constants,
  DomainDTO,
  EvaluationFlowsEnum,
  FreeServiceResult,
  NodeData,
  NoReplyTypes,
  ReplyToTypes,
  ResultInfo
} from "../models/shared.model";
import { CorrespondenceAnalyzer, DomainAgeAnalyzer, NoReplyAnalyzer, ReplyToAnalyzer } from "../analyzers";
import { RiskLevel } from "../models/risk-level";
import { StepResult } from "../models/step-result";
import { StringUtils } from "../utils/string.utils";
import { IAnalyzerConfig } from "../interfaces/analyzer-config";
import { ANALYZERS, HEADERS, HEADERS_KEYS, NodeResultDecisions } from "../models/constants";
import { FlowNodeNamesEnum } from "../models/flow";
import { environment } from "../../environments/environment";
import { firstValueFrom } from "rxjs";

export class SentAsYou extends StepProcess {
  constructor(props) {
    super(props);

  }

  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve) => {
      const sender = data.senderEmail.toLowerCase();
      const owner = await this.sharedService.getOwnerOfSharedFolder(this.run);
      let receiver = Office.context.mailbox.userProfile.emailAddress.toLowerCase();

      //If this is a shared folder we use the shared folder email as the receiver
      if(owner != null)
        receiver = owner;

      let result = false;

      if(sender == receiver)
        result = await this.isNotInSentFolder();

      const reasonLog = this.translate.instant(result ? "sender_flow.sent_as_you_true" : "sender_flow.sent_as_you_false");

      resolve(new StepResult(result, reasonLog));
    });
  };

  private async isNotInSentFolder() {
    const haveSentFolder = await this.sharedFolderHaveSentFolder();
    if(!haveSentFolder)
      return true;

    let internetMsgId = encodeURIComponent(this.run.item.internetMessageId);
    let res: any = await firstValueFrom(
      this.http.get(`${environment.apiConfig.microsoft_graph_url_personal}/mailFolders/sentitems/messages?$filter=internetMessageId eq '${internetMsgId}'&$top=1`)
    );

    return !res.value || res.value.length == 0;
  }

  private async sharedFolderHaveSentFolder(){
    let foldersRes: any = await firstValueFrom(this.http.get(`${environment.apiConfig.microsoft_graph_url_personal}/mailFolders`));
    let folders = foldersRes.value as []

    let sentFolder = folders.find((value: any) => value?.displayName.match(/^(sent\s?items?)$/i));
    return !!sentFolder;
  }
}

export class IsSenderDomainTrusted extends StepProcess {

  constructor(props) {
    super(props);

  }

  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        let reasonLog = "";
        const authHeader = this.messageService.getMessageHeaderValue(HEADERS.AUTHENTICATION_RESULTS);
        let smtpOrigin = this.getSmtpOrigin(authHeader);
        const smtpMatchSender = data.senderEmail.toLowerCase().includes(smtpOrigin.toLowerCase());

        let trustedDomains = this.domainService.getDomains();

        let domainDto = trustedDomains.find(value => StringUtils.compareCaseInsensitive(value.domain, data.domain));
        let isSenderDomainTrusted = !!domainDto;

        let isPersonalTrusted: boolean | undefined = undefined;

        let senderInfo: ResultInfo = {
          ...info,
          isDomainTrusted: isSenderDomainTrusted,
          isDomainPersonalTrusted: isPersonalTrusted,
          smtpOrigin: smtpOrigin,
          smtpOriginMatchSender: smtpMatchSender
        };

        if(isSenderDomainTrusted) {
          reasonLog = this.translate.instant("sender_flow.trusted_domain", { domain: data.domain });
        }
        else{
          senderInfo = this.getPersonalTrustedDomain(data.domain, data.senderEmail, {... senderInfo});
          const personalDomain = senderInfo.isDomainPersonalTrusted || senderInfo.isSenderEmailInPersonalTrusted;

          if (personalDomain) {
            isSenderDomainTrusted = true;

            domainDto = { domain: senderInfo.isDomainPersonalTrusted ? data.domain : data.senderEmail };

            reasonLog = this.translate.instant(senderInfo.isDomainPersonalTrusted ? "sender_flow.personal_trusted_domain"
              : "sender_flow.personal_trusted_email_address", { domain: senderInfo.isDomainPersonalTrusted ? data.domain : data.senderEmail });
          } else {
            reasonLog = this.translate.instant("sender_flow.not_trusted_domain", { domain: data.domain });
          }
        }

        if (isSenderDomainTrusted) {
          senderInfo = await this.domainRegisteredDT(domainDto, data.domain, {...senderInfo});
        }

       senderInfo.topLevelDomainCategory = this.checkTLDCategory(data)

        resolve(new StepResult(isSenderDomainTrusted, reasonLog, { ...senderInfo }));
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_sender_domain_trusted", { error: e }));
        reject(e);
      }
    });
  };

  private getPersonalTrustedDomain(domain: string, email: string, senderInfo: ResultInfo) {
    const personalTrustedList = this.domainService.loadPersonalTrustedListFromCache();
    const trustedDomain = personalTrustedList.domains.get(domain.toLowerCase());
    const trustedEmail = personalTrustedList.emails.get(email.toLowerCase());

    senderInfo.isDomainTrusted = !!trustedDomain;
    senderInfo.isDomainPersonalTrusted = !!trustedDomain;
    senderInfo.isSenderEmailInPersonalTrusted = !!trustedEmail;

    return senderInfo
  }

  private async domainRegisteredDT(result: DomainDTO, domain: string, senderInfo: ResultInfo) {
    const config: IAnalyzerConfig = this.configService.getAnalyzerConfiguration(ANALYZERS.DOMAIN_AGE);
    let ageStr = Constants.TRUSTED_DOMAIN.toString();
    let isDomainNew = false;

    let registeredDT = result.register_dt;

    if(!registeredDT)
      registeredDT = await this.domainService.getDomainRegisteredDate(domain);

    if (registeredDT) {
      const domainAge = this.domainService.calculateDomainAge(registeredDT);
      ageStr = domainAge.formattedDate;
      isDomainNew = domainAge.months <= config.thresholds.high;
    }

    senderInfo.domainAge = ageStr;
    senderInfo.isDomainNew = isDomainNew;
    senderInfo.domainCreatedAt = registeredDT;

    return senderInfo;
  }

  private checkTLDCategory(data: NodeData, senderInfo?: ResultInfo) {
    const tldCategory = this.domainService.checkTLDCategory(data.topLevelDomain);

    if (tldCategory == null)
      return Constants.STANDARD_DOMAIN;

    else if (tldCategory)
      return Constants.TRUSTED_DOMAIN;
    else
      return Constants.DANGEROUS_DOMAIN;
  }

  private getSmtpOrigin(authHeader: string){
    let smtpOrigin = undefined;
    if (!authHeader) {
      smtpOrigin = Constants.NOT_PROVIDED;
    } else {
      smtpOrigin = StringUtils.parseFromAuthEmailHeader(authHeader, HEADERS_KEYS.SMTP_MAIL_FROM);

      if (!smtpOrigin)
        smtpOrigin = StringUtils.parseFromAuthEmailHeader(authHeader, HEADERS_KEYS.HEADER_FROM);

      if(!smtpOrigin)
        smtpOrigin = Constants.NOT_PROVIDED;
    }

    return smtpOrigin;
  }
}

export class IsFromFreeEmailService extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        let reasonLog = "";
        const freeEmailServices: FreeServiceResult[] = this.domainService.getFreeEmailServices();

        let freeService: FreeServiceResult = freeEmailServices.find(e => e.domain === data.domain.toLowerCase());
        if (freeService) {
          reasonLog = this.translate.instant("sender_flow.free_email_service", { domain: data.domain });
          if (freeService.domain_register_dt) {
            const domainAge = this.domainService.calculateDomainAge(freeService.domain_register_dt);
            const config: IAnalyzerConfig = this.configService.getAnalyzerConfiguration(ANALYZERS.DOMAIN_AGE);
            info.domainAge = domainAge.formattedDate;
            info.isDomainNew = domainAge.months <= config.thresholds.high;
            info.domainCreatedAt = freeService.domain_register_dt
          } else {
            info.domainAge = Constants.FREE_SERVICE;
            info.isDomainNew = false;
          }
        } else {
          reasonLog = this.translate.instant("sender_flow.not_free_email_service", { domain: data.domain });
        }
        resolve(new StepResult((!!freeService), reasonLog, { ...info }));
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_from_free_email_service", { error: e }));
        reject(e);
      }
    });
  };

}

export class IsTopLevelDomainTrusted extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        const trustedTopLevel = this.domainService.getDomains(true, true);

        let result = trustedTopLevel.some(value =>
          StringUtils.compareCaseInsensitive(value.domain, data.topLevelDomain));

        let reasonLog = this.translate.instant(
            result ? "sender_flow.trusted_top_level_domain" : "sender_flow.not_trusted_top_level_domain",
            { topLevelDomain: data.topLevelDomain }
        );
        resolve(new StepResult(result, reasonLog));
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_top_level_domain_check", { error: e }));
        reject(e);
      }
    });
  };

}

export class IsDangerousTopLevelDomain extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        const dangerousTopLevel = this.domainService.getDomains(true, false);

        let result = dangerousTopLevel.some(value =>
          StringUtils.compareCaseInsensitive(value.domain, data.topLevelDomain));

        let reasonLog = result
            ? this.translate.instant("sender_flow.dangerous_top_level_domain", { topLevelDomain: data.topLevelDomain })
            : this.translate.instant("sender_flow.not_dangerous_top_level_domain", { topLevelDomain: data.topLevelDomain });
        resolve(new StepResult(result, reasonLog));
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_dangerous_top_level_domain", { error: e }));
        reject(e);
      }
    });
  };

}

export class IsFromBlacklistDomain extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {

        const blackListDomains: string[] =
          await this.domainService.getBlackListDomains();

        let result = blackListDomains.includes(data.domain.toLowerCase());
        let reasonLog = result
            ? this.translate.instant("sender_flow.domain_blacklisted", { domain: data.domain })
            : this.translate.instant("sender_flow.domain_not_blacklisted", { domain: data.domain });        resolve(new StepResult(result, reasonLog));
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_from_blacklist_check", { error: e }));
        reject(e);
      }
    });
  };

}

export class IsPriorContactWithSender extends StepProcess {
  constructor(props) {
    super(props);
  }


  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        const correspondenceAnalyzer = new CorrespondenceAnalyzer(this.injector);

        const response = await this.callWithTimeout(correspondenceAnalyzer.runAnalysis.bind(correspondenceAnalyzer),
          info, EvaluationFlowsEnum.SENDER, FlowNodeNamesEnum.isPriorContactWithSender, Constants.DEFAULT_CHECK_TIMEOUT_BEFORE_PENDING);

        if (response === "pending") {
          console.log(this.translate.instant("sender_flow.pending_result", {
            timeout: Constants.DEFAULT_CHECK_TIMEOUT_BEFORE_PENDING / 1000,
            flowNode: FlowNodeNamesEnum.isPriorContactWithSender
          }));

          resolve(new StepResult(NodeResultDecisions.PENDING, "Pending", info));

        } else {
          console.log(this.translate.instant("sender_flow.response_received", {
            flowNode: FlowNodeNamesEnum.isPriorContactWithSender
          }));

          info = { ...info, ...response["additionalInfo"] };

          resolve(new StepResult(response["level"] == RiskLevel.Low, response["additionalInfo"].info, info,
            EvaluationFlowsEnum.SENDER, FlowNodeNamesEnum.isPriorContactWithSender));
        }
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_prior_contact", {
          error: e
        }));
        reject(e);
      }
    });
  };

}

export class GetDomainAge extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        let domainAgeAnalyzer = new DomainAgeAnalyzer(this.injector);

        // Call the function with a DEFAULT_CHECK_TIMEOUT_BEFORE_PENDING-second timeout
        const response = await this.callWithTimeout(domainAgeAnalyzer.runAnalysis.bind(domainAgeAnalyzer), info, EvaluationFlowsEnum.SENDER, FlowNodeNamesEnum.getDomainAge,
          Constants.DEFAULT_CHECK_TIMEOUT_BEFORE_PENDING);

        if (response === "pending") {
          console.log(this.translate.instant("sender_flow.pending_result", {
            timeout: Constants.DEFAULT_CHECK_TIMEOUT_BEFORE_PENDING / 1000,
            flowNode: FlowNodeNamesEnum.getDomainAge
          }));

          resolve(new StepResult(NodeResultDecisions.PENDING, "Pending", info));

        } else {
          console.log(this.translate.instant("sender_flow.response_received", {
            flowNode: FlowNodeNamesEnum.isPriorContactWithSender
          }));
          info = { ...info, ...response["additionalInfo"] };

          let age = response['level'] == RiskLevel.Low ? NodeResultDecisions.OLD :
            (response['level'] == RiskLevel.Moderate ? NodeResultDecisions.PENDING : NodeResultDecisions.NEW);

          resolve(new StepResult(age, response["additionalInfo"].info, info));
        }
      } catch (e) {
        console.log(this.translate.instant("sender_flow.error_get_domain_age", { error: e }));
        reject(e);
      }
    });
  };

}

export class CheckNoReplySender extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {

      try {
        let noReplyAnalyzer = new NoReplyAnalyzer(this.injector);
        let res = await noReplyAnalyzer.runAnalysis();

        let result = res.level == RiskLevel.Low ? NoReplyTypes.MATCH
          : (res.level == RiskLevel.Moderate ? NoReplyTypes.NO_MATCH_BUT_TRUSTED : NoReplyTypes.NO_MATCH_AND_UNTRUSTED);

        resolve(new StepResult(result, res.additionalInfo, info,
          EvaluationFlowsEnum.SENDER, FlowNodeNamesEnum.checkNoReplySender));
      } catch (e) {
        console.log(this.translate.instant('sender_flow.error_in_no_reply_sender', {error: e }));
        reject(e);
      }
    });
  };
}

export class CheckReplyToField extends StepProcess {
  constructor(props) {
    super(props);
  }

  process = async (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {

      try {
        let replyToAnalyzer = new ReplyToAnalyzer(this.injector);
        let res = await replyToAnalyzer.runAnalysis();

        let result = res.level == RiskLevel.Low ? ReplyToTypes.NONE_OR_SAME
          : (res.level == RiskLevel.Moderate ? ReplyToTypes.DIFFERENT_BUT_SAME : ReplyToTypes.DIFFERENT);


        resolve(new StepResult(result, `${res.additionalInfo.join(" and ")}`, {
          ...info, replyTo: replyToAnalyzer.getReplyTo(),
          replyToMatchSender: result == ReplyToTypes.NONE_OR_SAME
        }));
      } catch (e) {
        console.log(this.translate.instant('sender_flow.error_in_check_reply_to_field', {error: e }));
        reject(e);
      }
    });
  };
}
